import React, { useState } from 'react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import useFacilityFeatures from '../../hooks/useFacilityFeatures';
import AssessmentTools from './AssessmentTools';
import AssessmentLibraryPanel from './AssessmentLibraryPanel';
import AssessmentTrendCharts from './AssessmentTrendCharts';
import IncidentReporting from './IncidentReporting';
import PalliativeCareTemplates from './PalliativeCareTemplates';
import ANACCAssessment from './ANACCAssessment';
import { 
  Stethoscope, Plus, Search, Filter, Calendar, AlertTriangle, 
  CheckCircle, Clock, Eye, Edit3, UserCheck, Target,
  TrendingUp, TrendingDown, FileText, Download, Printer,
  RefreshCw, Bell, Award, Star, Activity, Users,
  ChevronLeft, ChevronRight, MoreVertical, ClipboardList,
  AlertCircle, Heart, Brain, Activity as ActivityIcon,
  Shield, Zap, BarChart3, Settings, User, MapPin,
  ArrowRight, ArrowUp, ArrowDown, Minus, X, Check, Flag
} from 'lucide-react';

const Clinical = () => {
  const { state } = useAppContext();
  const actions = useAppActions();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;
  
  // Get facility features for conditional UI
  const { isAgedCare, supportsANACC, facilityConfig, facilityId } = useFacilityFeatures();

  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentModalType, setAssessmentModalType] = useState('generic'); // 'generic' or 'anacc'
  const [showCarePlanModal, setShowCarePlanModal] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showAssessmentTools, setShowAssessmentTools] = useState(false);
  const [showIncidentReporting, setShowIncidentReporting] = useState(false);
  const [showPalliativeCareTemplates, setShowPalliativeCareTemplates] = useState(false);

  // Enhanced clinical stats with more comprehensive data
  const clinicalStats = {
    assessmentsDue: { count: 12, trend: '+3', timeframe: 'This week', priority: 'High', color: 'red' },
    carePlans: { count: 8, status: 'Need review', overdue: 2, completion: 75, color: 'orange' },
    incidents: { count: 3, timeframe: 'Last 7 days', trend: '-25%', severity: 'Low', color: 'yellow' },
    documentation: { percentage: 98, status: 'Compliance', trend: '+2%', target: 95, color: 'green' },
    qualityScore: { score: 94, trend: '+5%', status: 'Excellent', color: 'blue' },
    safetyIndex: { score: 96, trend: '+2%', status: 'High', color: 'purple' }
  };

  const clinicalNotes = [
    {
      id: 'CN001',
      residentName: 'Margaret Johnson',
      room: 'Room 101',
      lastAssessment: '20 Jan 2024',
      nextAssessment: '20 Feb 2024',
      metrics: {
        cognitive: { score: 18, max: 30, status: 'moderate impairment', trend: 'declining' },
        physical: { score: 75, max: 100, status: 'good', trend: 'stable' },
        nutrition: { status: 'At Risk', alert: true, bmi: 22.5 },
        fallsRisk: { status: 'High', alert: true, score: 8 }
      },
      notes: 'Recent cognitive decline noted. Increased supervision required. Family informed of changes.',
      recommendations: [
        'Increase supervision during ADLs',
        'Review medication for cognitive enhancement',
        'Implement additional safety measures',
        'Schedule family meeting to discuss care plan changes'
      ],
      doctor: 'Dr. Sarah Mitchell',
      nurse: 'Sarah Johnson',
      priority: 'High',
      status: 'Active',
      alerts: ['Cognitive Decline', 'Fall Risk'],
      lastUpdated: '2 hours ago'
    },
    {
      id: 'CN002',
      residentName: 'Robert Wilson',
      room: 'Room 205',
      lastAssessment: '15 Jan 2024',
      nextAssessment: '15 Feb 2024',
      metrics: {
        cognitive: { score: 12, max: 30, status: 'severe impairment', trend: 'declining' },
        physical: { score: 60, max: 100, status: 'poor', trend: 'declining' },
        nutrition: { status: 'At Risk', alert: true, bmi: 19.8 },
        fallsRisk: { status: 'Critical', alert: true, score: 10 }
      },
      notes: 'Significant cognitive and physical decline. Requires specialized dementia care. End-of-life planning discussed with family.',
      recommendations: [
        'Implement comfort care measures',
        'Review pain management protocol',
        'Increase family involvement in care',
        'Prepare advance care planning documents'
      ],
      doctor: 'Dr. Michael Chen',
      nurse: 'Lisa Brown',
      priority: 'Critical',
      status: 'Urgent',
      alerts: ['Severe Cognitive Decline', 'Critical Fall Risk', 'Nutrition Risk'],
      lastUpdated: '1 hour ago'
    },
    {
      id: 'CN003',
      residentName: 'Elizabeth Brown',
      room: 'Room 103',
      lastAssessment: '18 Jan 2024',
      nextAssessment: '18 Feb 2024',
      metrics: {
        cognitive: { score: 22, max: 30, status: 'mild impairment', trend: 'stable' },
        physical: { score: 70, max: 100, status: 'fair', trend: 'improving' },
        nutrition: { status: 'Well Nourished', alert: false, bmi: 24.2 },
        fallsRisk: { status: 'Medium', alert: false, score: 5 }
      },
      notes: 'Stable condition with slight improvement in mobility. Continue current care plan.',
      recommendations: [
        'Continue physical therapy program',
        'Monitor medication effectiveness',
        'Encourage participation in activities',
        'Regular family updates'
      ],
      doctor: 'Dr. Emily Wilson',
      nurse: 'Emma Davis',
      priority: 'Medium',
      status: 'Stable',
      alerts: [],
      lastUpdated: '4 hours ago'
    },
    {
      id: 'CN004',
      residentName: 'James Anderson',
      room: 'Room 108',
      lastAssessment: '22 Jan 2024',
      nextAssessment: '22 Feb 2024',
      metrics: {
        cognitive: { score: 25, max: 30, status: 'mild impairment', trend: 'improving' },
        physical: { score: 85, max: 100, status: 'excellent', trend: 'improving' },
        nutrition: { status: 'Well Nourished', alert: false, bmi: 26.1 },
        fallsRisk: { status: 'Low', alert: false, score: 3 }
      },
      notes: 'Excellent progress in physical therapy. Cognitive function showing improvement with new medication regimen.',
      recommendations: [
        'Continue current medication regimen',
        'Maintain physical therapy schedule',
        'Encourage social activities',
        'Monitor cognitive improvements'
      ],
      doctor: 'Dr. Jennifer Lee',
      nurse: 'Michael Torres',
      priority: 'Low',
      status: 'Improving',
      alerts: [],
      lastUpdated: '6 hours ago'
    },
    {
      id: 'CN005',
      residentName: 'Dorothy Smith',
      room: 'Room 112',
      lastAssessment: '19 Jan 2024',
      nextAssessment: '19 Feb 2024',
      metrics: {
        cognitive: { score: 20, max: 30, status: 'moderate impairment', trend: 'stable' },
        physical: { score: 65, max: 100, status: 'fair', trend: 'stable' },
        nutrition: { status: 'At Risk', alert: true, bmi: 20.3 },
        fallsRisk: { status: 'High', alert: true, score: 7 }
      },
      notes: 'Stable condition but requires close monitoring. Nutrition concerns addressed with dietary modifications.',
      recommendations: [
        'Implement nutritional supplements',
        'Increase fall prevention measures',
        'Regular weight monitoring',
        'Family education on nutrition needs'
      ],
      doctor: 'Dr. David Kim',
      nurse: 'Sarah Wilson',
      priority: 'High',
      status: 'Monitoring',
      alerts: ['Nutrition Risk', 'Fall Risk'],
      lastUpdated: '3 hours ago'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-800';
      case 'good': return 'bg-blue-100 text-blue-800';
      case 'normal': return 'bg-yellow-100 text-yellow-800';
      case 'concern': return 'bg-orange-100 text-orange-800';
      case 'poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAlertColor = (alert) => {
    return alert ? 'text-red-600 font-semibold' : 'text-gray-600';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Urgent': return 'bg-red-100 text-red-800';
      case 'Active': return 'bg-orange-100 text-orange-800';
      case 'Stable': return 'bg-green-100 text-green-800';
      case 'Improving': return 'bg-blue-100 text-blue-800';
      case 'Monitoring': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewNotes = (note) => {
    setSelectedNote(note);
    setShowNotesModal(true);
  };

  const handleNewAssessment = () => {
    setAssessmentModalType('generic');
    setShowAssessmentModal(true);
  };

  const handleANACCAssessment = () => {
    setAssessmentModalType('anacc');
    setSelectedResident(null); // Reset resident selection
    setShowAssessmentModal(true);
  };

  const handleNewCarePlan = () => {
    setShowCarePlanModal(true);
  };

  const filteredNotes = clinicalNotes.filter(note => {
    const matchesSearch = note.residentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         note.room.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-red-600 via-pink-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-10"></div>
        <div className="relative z-10">
        <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Stethoscope className="w-8 h-8 text-white animate-pulse" />
              </div>
          <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
              Clinical Care Management
                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
            </h1>
                <p className="text-base mt-2 opacity-90">
              {'Comprehensive clinical documentation and care planning'}
            </p>
              {facilityConfig && (
                <div className="mt-2 flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    
                    isAgedCare ? 'bg-purple-200 text-purple-800' : 
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {isAgedCare ? 'Aged Care' : 'General'}
                  </span>
                </div>
              )}
              </div>
          </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* AN-ACC Assessment - Aged Care ONLY (Mandatory for funding classification) */}
              {supportsANACC && isAgedCare && (
                <button 
                  onClick={handleANACCAssessment}
                  className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-opacity-30 transition-all duration-300 flex items-center gap-2 backdrop-blur-sm border border-white border-opacity-30 group relative"
                  title="AN-ACC Assessment: Mandatory Australian National Aged Care Classification tool for resident funding classification. Includes RUG-ADL, Rockwood Frailty Scale, Braden Scale, and DEMMI Score assessments."
                >
                  <Plus className="w-5 h-5" />
                  <span>AN-ACC Assessment</span>
                  <span className="ml-1 text-xs bg-yellow-500 bg-opacity-80 px-2 py-0.5 rounded-full">Required</span>
                </button>
              )}
              
              {/* Clinical Assessment - Aged Care Only */}
              {isAgedCare && (
                <button 
                  onClick={handleNewAssessment}
                  className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-opacity-30 transition-all duration-300 flex items-center gap-2 backdrop-blur-sm border border-white border-opacity-30 group relative"
                  title={
                    isAgedCare 
                      ? "Clinical Assessment: Routine clinical assessments including fall risk, cognitive, nutrition, and functional assessments for Aged Care residents."
                      : "Health Assessment: Functional assessments, health reviews, and goal-based health evaluations for NDIS participants."
                  }
                >
                  <Plus className="w-5 h-5" />
                  <span>
                    {'Clinical Assessment'}
                  </span>
                </button>
              )}
              
              {/* Care Plan - Aged Care Only */}
              {isAgedCare && (
                <button 
                  onClick={handleNewCarePlan}
                  className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-opacity-30 transition-all duration-300 flex items-center gap-2 backdrop-blur-sm border border-white border-opacity-30 group relative"
                  title={
                    isAgedCare
                      ? "Care Plan: Clinical care planning document outlining resident care goals, interventions, and outcomes for Aged Care compliance."
                      : "Support Plan: Goal-based support planning document aligned with NDIS participant goals and funded supports."
                  }
                >
                  <FileText className="w-5 h-5" />
                  <span>
                    {'New Care Plan'}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Clinical Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Assessments Due */}
        <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-red-200 hover:shadow-lg hover:border-red-300 transition-all duration-300 h-[140px] flex flex-col overflow-hidden relative">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 relative z-10">
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full flex-shrink-0 relative z-10">
              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-700 font-bold text-xs">{clinicalStats.assessmentsDue.trend}</span>
            </div>
          </div>
          <div className="mt-auto">
            <h3 className="text-3xl font-extrabold text-red-700 mb-1 leading-none">{clinicalStats.assessmentsDue.count}</h3>
            <p className="text-red-600 font-semibold text-sm mb-0.5">Assessments Due</p>
            <p className="text-xs text-gray-500">{clinicalStats.assessmentsDue.timeframe}</p>
          </div>
        </div>
        
        {/* Care Plans */}
        <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-orange-200 hover:shadow-lg hover:border-orange-300 transition-all duration-300 h-[140px] flex flex-col overflow-hidden relative">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 relative z-10">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded-full flex-shrink-0 relative z-10">
              <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
              <span className="text-red-700 font-bold text-xs">{clinicalStats.carePlans.overdue}</span>
            </div>
          </div>
          <div className="mt-auto">
            <h3 className="text-3xl font-extrabold text-orange-700 mb-1 leading-none">{clinicalStats.carePlans.count}</h3>
            <p className="text-orange-600 font-semibold text-sm mb-0.5">Care Plans</p>
            <p className="text-xs text-gray-500">{clinicalStats.carePlans.status}</p>
          </div>
        </div>
        
        {/* Incidents */}
        <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-yellow-200 hover:shadow-lg hover:border-yellow-300 transition-all duration-300 h-[140px] flex flex-col overflow-hidden relative">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 relative z-10">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full flex-shrink-0 relative z-10">
              <TrendingDown className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-700 font-bold text-xs">{clinicalStats.incidents.trend}</span>
            </div>
          </div>
          <div className="mt-auto">
            <h3 className="text-3xl font-extrabold text-yellow-700 mb-1 leading-none">{clinicalStats.incidents.count}</h3>
            <p className="text-yellow-600 font-semibold text-sm mb-0.5">Incidents</p>
            <p className="text-xs text-gray-500">{clinicalStats.incidents.timeframe}</p>
          </div>
        </div>
        
        {/* Documentation */}
        <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-green-200 hover:shadow-lg hover:border-green-300 transition-all duration-300 h-[140px] flex flex-col overflow-hidden relative">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 relative z-10">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full flex-shrink-0 relative z-10">
              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-700 font-bold text-xs">{clinicalStats.documentation.trend}</span>
            </div>
          </div>
          <div className="mt-auto">
            <h3 className="text-3xl font-extrabold text-green-700 mb-1 leading-none">{clinicalStats.documentation.percentage}%</h3>
            <p className="text-green-600 font-semibold text-sm mb-0.5">Documentation</p>
            <p className="text-xs text-gray-500">{clinicalStats.documentation.status}</p>
          </div>
        </div>

        {/* Quality Score */}
        <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-blue-200 hover:shadow-lg hover:border-blue-300 transition-all duration-300 h-[140px] flex flex-col overflow-hidden relative">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 relative z-10">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full flex-shrink-0 relative z-10">
              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-700 font-bold text-xs">{clinicalStats.qualityScore.trend}</span>
            </div>
          </div>
          <div className="mt-auto">
            <h3 className="text-3xl font-extrabold text-blue-700 mb-1 leading-none">{clinicalStats.qualityScore.score}</h3>
            <p className="text-blue-600 font-semibold text-sm mb-0.5">Quality Score</p>
            <p className="text-xs text-gray-500">{clinicalStats.qualityScore.status}</p>
          </div>
        </div>

        {/* Safety Index */}
        <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-purple-200 hover:shadow-lg hover:border-purple-300 transition-all duration-300 h-[140px] flex flex-col overflow-hidden relative">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 relative z-10">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full flex-shrink-0 relative z-10">
              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
              <span className="text-green-700 font-bold text-xs">{clinicalStats.safetyIndex.trend}</span>
            </div>
          </div>
          <div className="mt-auto">
            <h3 className="text-3xl font-extrabold text-purple-700 mb-1 leading-none">{clinicalStats.safetyIndex.score}</h3>
            <p className="text-purple-600 font-semibold text-sm mb-0.5">Safety Index</p>
            <p className="text-xs text-gray-500">{clinicalStats.safetyIndex.status}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Stethoscope, color: 'red' },
              { id: 'assessments', label: 'Assessments', icon: ClipboardList, color: 'blue' },
              { id: 'careplans', label: 'Care Plans', icon: FileText, color: 'green' },
              { id: 'notes', label: 'Clinical Notes', icon: ActivityIcon, color: 'purple' },
              { id: 'analytics', label: 'Analytics', icon: BarChart3, color: 'indigo' }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 py-3 px-4 rounded-xl font-medium text-sm transition-all duration-300 ${
                    isActive
                      ? `bg-${tab.color}-500 text-white shadow-lg transform scale-105`
                      : `text-gray-600 hover:text-${tab.color}-600 hover:bg-${tab.color}-50 hover:shadow-md`
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-white' : ''}`} />
                  {tab.label}
                  {isActive && (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-8">
          {/* Search and Filters */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 relative">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                    placeholder="Search residents, assessments, notes, care plans..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 text-lg"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
            </div>
              <div className="flex flex-wrap gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-6 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-300 bg-white font-medium"
              >
                <option value="all">All Status</option>
                <option value="normal">Normal</option>
                <option value="concern">Concern</option>
                <option value="alert">Alert</option>
                  <option value="critical">Critical</option>
              </select>
                <button className="flex items-center gap-3 px-6 py-4 border-2 border-gray-200 rounded-xl hover:bg-white hover:border-red-500 hover:text-red-600 transition-all duration-300 font-medium">
                  <Calendar className="w-5 h-5" />
                Date Range
              </button>
                <button className="flex items-center gap-3 px-6 py-4 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all duration-300 font-medium shadow-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </button>
              </div>
            </div>
          </div>

          {/* Content based on active tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Quick Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-blue-500 rounded-xl">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-blue-800">24</span>
                  </div>
                  <h3 className="font-semibold text-blue-800 mb-2">Active Residents</h3>
                  <p className="text-sm text-blue-600">All wards combined</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-green-500 rounded-xl">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-green-800">18</span>
                  </div>
                  <h3 className="font-semibold text-green-800 mb-2">Completed Today</h3>
                  <p className="text-sm text-green-600">Assessments & care tasks</p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-orange-500 rounded-xl">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-orange-800">6</span>
                  </div>
                  <h3 className="font-semibold text-orange-800 mb-2">Pending Review</h3>
                  <p className="text-sm text-orange-600">Requires attention</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200 shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-purple-500 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-2xl font-bold text-purple-800">94%</span>
                  </div>
                  <h3 className="font-semibold text-purple-800 mb-2">Quality Score</h3>
                  <p className="text-sm text-purple-600">This month</p>
                </div>
              </div>

              {/* Recent Activity and Alerts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-blue-500 rounded-xl">
                      <ClipboardList className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-800">Recent Assessments</h3>
                  </div>
                  <div className="space-y-4">
                    {clinicalNotes.slice(0, 4).map((note, index) => (
                      <div key={note.id} className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{note.residentName}</h4>
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              {note.room}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(note.priority)}`}>
                              {note.priority}
                          </span>
                            <span className="text-xs text-gray-500">{note.lastUpdated}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">Last: {note.lastAssessment}</span>
                          <span className="text-gray-600">Next: {note.nextAssessment}</span>
                        </div>
                        {note.alerts.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {note.alerts.map((alert, alertIndex) => (
                              <span key={alertIndex} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                {alert}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-green-500 rounded-xl">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-green-800">Care Plan Status</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">Active Care Plans</span>
                        <span className="text-2xl font-bold text-green-600">8</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: '80%'}}></div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">Due for Review</span>
                        <span className="text-2xl font-bold text-orange-600">3</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{width: '30%'}}></div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">Overdue</span>
                        <span className="text-2xl font-bold text-red-600">2</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-red-500 h-2 rounded-full" style={{width: '20%'}}></div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">Completion Rate</span>
                        <span className="text-2xl font-bold text-blue-600">75%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{width: '75%'}}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Reporting Section */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-red-800 flex items-center gap-3">
                      <AlertTriangle className="w-7 h-7 text-red-600" />
                      Incident Reporting & Management
                    </h3>
                    <p className="text-red-600 mt-1">Comprehensive incident tracking, documentation, and escalation system</p>
                  </div>
                  <button
                    onClick={() => setShowIncidentReporting(true)}
                    className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 shadow-lg"
                  >
                    <Plus className="w-5 h-5" />
                    Open Incident Reporting
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-gray-800">High Severity</h4>
                    </div>
                    <p className="text-sm text-gray-600">Track and manage high-priority incidents requiring immediate attention</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-orange-600" />
                      <h4 className="font-semibold text-gray-800">Documentation</h4>
                    </div>
                    <p className="text-sm text-gray-600">Comprehensive incident documentation with photo and file attachments</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-yellow-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Flag className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-semibold text-gray-800">Escalation</h4>
                    </div>
                    <p className="text-sm text-gray-600">Automated escalation workflows for critical incidents</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-800">Analytics</h4>
                    </div>
                    <p className="text-sm text-gray-600">Incident trends, patterns, and improvement insights</p>
                  </div>
                </div>
              </div>

              {/* Palliative Care Templates Section */}
              <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 border border-pink-200 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-pink-800 flex items-center gap-3">
                      <Heart className="w-7 h-7 text-pink-600" />
                      Palliative Care Templates
                    </h3>
                    <p className="text-pink-600 mt-1">Specialized care planning templates for end-of-life and palliative care</p>
                  </div>
                  <button
                    onClick={() => setShowPalliativeCareTemplates(true)}
                    className="bg-pink-600 text-white px-6 py-3 rounded-lg hover:bg-pink-700 transition-colors flex items-center gap-2 shadow-lg"
                  >
                    <Plus className="w-5 h-5" />
                    Open Palliative Care
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-pink-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Heart className="w-5 h-5 text-pink-600" />
                      <h4 className="font-semibold text-gray-800">Comfort Care</h4>
                    </div>
                    <p className="text-sm text-gray-600">End-of-life care focusing on comfort, dignity, and family support</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Stethoscope className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-800">Symptom Management</h4>
                    </div>
                    <p className="text-sm text-gray-600">Pain management and symptom control protocols</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-purple-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-gray-800">Psychosocial Care</h4>
                    </div>
                    <p className="text-sm text-gray-600">Family support and communication frameworks</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-800">Care Plans</h4>
                    </div>
                    <p className="text-sm text-gray-600">Comprehensive palliative care planning templates</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'assessments' && (
            <div className="space-y-8">
              {/* Assessment Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-blue-500 rounded-xl">
                      <ClipboardList className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-blue-800">12</h3>
                      <p className="text-blue-600 font-medium">Due This Week</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>+3 from last week</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-green-500 rounded-xl">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-green-800">18</h3>
                      <p className="text-green-600 font-medium">Completed Today</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>85% completion rate</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-orange-500 rounded-xl">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-orange-800">3</h3>
                      <p className="text-orange-600 font-medium">Overdue</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <Clock className="w-4 h-4" />
                    <span>Requires immediate attention</span>
                  </div>
                </div>
              </div>

              <AssessmentLibraryPanel context="clinical" />

              <AssessmentTrendCharts mode="facility" facilityKey={facilityId || 'all'} />

              {/* Assessment Tools Section */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-indigo-800 flex items-center gap-3">
                      <Activity className="w-7 h-7 text-indigo-600" />
                      Clinical Assessment Tools
                    </h3>
                    <p className="text-indigo-600 mt-1">Comprehensive assessment tools for falls risk, pressure injury, nutrition, and cognitive evaluation</p>
                  </div>
                  <button
                    onClick={() => setShowAssessmentTools(true)}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 shadow-lg"
                  >
                    <Plus className="w-5 h-5" />
                    Open Assessment Tools
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-red-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-gray-800">Falls Risk</h4>
                    </div>
                    <p className="text-sm text-gray-600">Assess fall risk factors and implement prevention strategies</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-orange-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="w-5 h-5 text-orange-600" />
                      <h4 className="font-semibold text-gray-800">Pressure Injury</h4>
                    </div>
                    <p className="text-sm text-gray-600">Evaluate pressure injury risk and skin integrity</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Heart className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-gray-800">Nutrition</h4>
                    </div>
                    <p className="text-sm text-gray-600">Assess nutritional status and dietary needs</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3 mb-2">
                      <Brain className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-800">Cognitive</h4>
                    </div>
                    <p className="text-sm text-gray-600">Evaluate cognitive function and mental health</p>
                  </div>
                </div>
              </div>

              {/* Assessment Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredNotes.map((note) => (
                  <div key={`AS-${note.id}`} className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-red-300">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{note.residentName}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(note.priority)}`}>
                            {note.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {note.room}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(note.status)}`}>
                          {note.status}
                        </span>
                        <button 
                          onClick={() => handleViewNotes(note)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 p-3 rounded-xl">
                        <div className="text-sm text-gray-600 mb-1">Last Assessment</div>
                        <div className="font-semibold text-gray-900">{note.lastAssessment}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-xl">
                        <div className="text-sm text-gray-600 mb-1">Next Due</div>
                        <div className="font-semibold text-gray-900">{note.nextAssessment}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-red-50 p-3 rounded-xl border border-red-200">
                        <div className="text-sm text-red-600 mb-1">Falls Risk</div>
                        <div className={`font-semibold ${getAlertColor(note.metrics.fallsRisk.alert)}`}>
                          {note.metrics.fallsRisk.status}
                        </div>
                        <div className="text-xs text-red-500">Score: {note.metrics.fallsRisk.score}</div>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-xl border border-blue-200">
                        <div className="text-sm text-blue-600 mb-1">Cognitive</div>
                        <div className="font-semibold text-blue-900">
                          {note.metrics.cognitive.score}/{note.metrics.cognitive.max}
                        </div>
                        <div className="text-xs text-blue-500 capitalize">{note.metrics.cognitive.status}</div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-xl">
                      <div className="text-sm text-gray-600 mb-2">Key Recommendations:</div>
                      <div className="text-sm text-gray-800">
                        {note.recommendations.slice(0, 2).join(' • ')}
                      </div>
                    </div>

                    {note.alerts.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {note.alerts.map((alert, alertIndex) => (
                          <span key={alertIndex} className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                            ⚠️ {alert}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        Last updated: {note.lastUpdated}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Dr. {note.doctor.split(' ')[1]}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500">{note.nurse}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'careplans' && (
            <div className="space-y-8">
              {/* Care Plan Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-green-500 rounded-xl">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-green-800">8</h3>
                      <p className="text-green-600 font-medium">Active Plans</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>75% completion rate</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border border-orange-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-orange-500 rounded-xl">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-orange-800">3</h3>
                      <p className="text-orange-600 font-medium">Due for Review</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Next 7 days</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-2xl border border-red-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-red-500 rounded-xl">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-red-800">2</h3>
                      <p className="text-red-600 font-medium">Overdue</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <Clock className="w-4 h-4" />
                    <span>Requires attention</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-blue-500 rounded-xl">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-blue-800">94%</h3>
                      <p className="text-blue-600 font-medium">Quality Score</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <ArrowUp className="w-4 h-4" />
                    <span>+5% this month</span>
                  </div>
                </div>
              </div>

              {/* Care Plan Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredNotes.map((note, idx) => (
                  <div key={`CP-${note.id}`} className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-green-300">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{note.residentName}</h3>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Plan #{idx + 1}
                          </span>
                        </div>
                        <p className="text-gray-600 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {note.room}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(note.status)}`}>
                          {note.status}
                        </span>
                        <button 
                          onClick={() => handleViewNotes(note)}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-300"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl mb-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Care Recommendations:</h4>
                      <ul className="space-y-2">
                        {note.recommendations.slice(0, 4).map((rec, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-blue-50 p-3 rounded-xl border border-blue-200">
                        <div className="text-sm text-blue-600 mb-1">Next Review</div>
                        <div className="font-semibold text-blue-900">{note.nextAssessment}</div>
                      </div>
                      <div className="bg-green-50 p-3 rounded-xl border border-green-200">
                        <div className="text-sm text-green-600 mb-1">Care Team</div>
                        <div className="font-semibold text-green-900">{note.doctor}</div>
                        <div className="text-xs text-green-600">{note.nurse}</div>
                      </div>
                    </div>

                    {note.alerts.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {note.alerts.map((alert, alertIndex) => (
                          <span key={alertIndex} className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                            ⚠️ {alert}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        Last updated: {note.lastUpdated}
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="px-4 py-2 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors">
                          Update Plan
                        </button>
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-50 transition-colors">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-8">
              {/* Notes Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-purple-500 rounded-xl">
                      <ActivityIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-purple-800">24</h3>
                      <p className="text-purple-600 font-medium">Total Notes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-purple-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>+12 this week</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-blue-500 rounded-xl">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-blue-800">8</h3>
                      <p className="text-blue-600 font-medium">Updated Today</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Real-time updates</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-green-500 rounded-xl">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-green-800">98%</h3>
                      <p className="text-green-600 font-medium">Accuracy Rate</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>Quality maintained</span>
                  </div>
                </div>
              </div>

              {/* Clinical Notes Cards */}
              <div className="space-y-6">
                {filteredNotes.map((note) => (
                  <div key={note.id} className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-purple-300">
                    <div className="flex justify-between items-start mb-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-900">{note.residentName}</h3>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {note.room}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(note.priority)}`}>
                            {note.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Last assessment: {note.lastAssessment}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(note.status)}`}>
                          {note.status}
                        </span>
                        <button 
                          onClick={() => handleViewNotes(note)}
                          className="px-4 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View Details
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="space-y-4">
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-blue-900">Cognitive Assessment</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(note.metrics.cognitive.status)}`}>
                              {note.metrics.cognitive.score}/{note.metrics.cognitive.max}
                            </span>
                          </div>
                          <div className="text-sm text-blue-700 capitalize">{note.metrics.cognitive.status}</div>
                          <div className="flex items-center gap-1 mt-1">
                            {note.metrics.cognitive.trend === 'improving' ? (
                              <ArrowUp className="w-3 h-3 text-green-600" />
                            ) : note.metrics.cognitive.trend === 'declining' ? (
                              <ArrowDown className="w-3 h-3 text-red-600" />
                            ) : (
                              <Minus className="w-3 h-3 text-gray-600" />
                            )}
                            <span className="text-xs text-gray-600 capitalize">{note.metrics.cognitive.trend}</span>
                          </div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-green-900">Physical Health</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(note.metrics.physical.status)}`}>
                              {note.metrics.physical.score}/{note.metrics.physical.max}
                            </span>
                          </div>
                          <div className="text-sm text-green-700 capitalize">{note.metrics.physical.status}</div>
                          <div className="flex items-center gap-1 mt-1">
                            {note.metrics.physical.trend === 'improving' ? (
                              <ArrowUp className="w-3 h-3 text-green-600" />
                            ) : note.metrics.physical.trend === 'declining' ? (
                              <ArrowDown className="w-3 h-3 text-red-600" />
                            ) : (
                              <Minus className="w-3 h-3 text-gray-600" />
                            )}
                            <span className="text-xs text-gray-600 capitalize">{note.metrics.physical.trend}</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className={`p-4 rounded-xl border ${note.metrics.nutrition.alert ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className={`font-medium ${note.metrics.nutrition.alert ? 'text-red-900' : 'text-green-900'}`}>Nutrition Status</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertColor(note.metrics.nutrition.alert)}`}>
                              {note.metrics.nutrition.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">BMI: {note.metrics.nutrition.bmi}</div>
                          {note.metrics.nutrition.alert && (
                            <div className="text-xs text-red-600 mt-1">⚠️ Requires attention</div>
                          )}
                        </div>

                        <div className={`p-4 rounded-xl border ${note.metrics.fallsRisk.alert ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className={`font-medium ${note.metrics.fallsRisk.alert ? 'text-red-900' : 'text-yellow-900'}`}>Falls Risk</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertColor(note.metrics.fallsRisk.alert)}`}>
                              {note.metrics.fallsRisk.status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700">Score: {note.metrics.fallsRisk.score}/10</div>
                          {note.metrics.fallsRisk.alert && (
                            <div className="text-xs text-red-600 mt-1">⚠️ High risk - safety measures needed</div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-xl mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Clinical Notes:</h4>
                      <p className="text-gray-700 leading-relaxed">{note.notes}</p>
                    </div>

                    {note.alerts.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {note.alerts.map((alert, alertIndex) => (
                          <span key={alertIndex} className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                            ⚠️ {alert}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                      <div className="text-sm text-gray-500">
                        Last updated: {note.lastUpdated} • Dr. {note.doctor.split(' ')[1]} • {note.nurse}
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="px-4 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors">
                          Edit Notes
                        </button>
                        <button className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors">
                          Print
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-8">
              {/* Analytics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-6 rounded-2xl border border-indigo-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-indigo-500 rounded-xl">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-indigo-800">94%</h3>
                      <p className="text-indigo-600 font-medium">Quality Score</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-indigo-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>+5% this month</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border border-green-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-green-500 rounded-xl">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-green-800">98%</h3>
                      <p className="text-green-600 font-medium">Compliance Rate</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Award className="w-4 h-4" />
                    <span>Above target</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-blue-500 rounded-xl">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-blue-800">2.3</h3>
                      <p className="text-blue-600 font-medium">Avg Response Time</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <TrendingDown className="w-4 h-4" />
                    <span>-0.5 hours</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border border-purple-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-purple-500 rounded-xl">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-purple-800">24</h3>
                      <p className="text-purple-600 font-medium">Active Residents</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-purple-600">
                    <TrendingUp className="w-4 h-4" />
                    <span>+2 this month</span>
                  </div>
                </div>
              </div>

              {/* Charts and Detailed Analytics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Assessment Trends</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
                      <span className="font-medium text-blue-900">Completed Assessments</span>
                      <span className="text-2xl font-bold text-blue-600">156</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                      <span className="font-medium text-green-900">On-Time Completion</span>
                      <span className="text-2xl font-bold text-green-600">142</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-orange-50 rounded-xl">
                      <span className="font-medium text-orange-900">Overdue</span>
                      <span className="text-2xl font-bold text-orange-600">14</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Care Plan Effectiveness</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                      <span className="font-medium text-green-900">Successful Outcomes</span>
                      <span className="text-2xl font-bold text-green-600">89%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
                      <span className="font-medium text-blue-900">Plan Adherence</span>
                      <span className="text-2xl font-bold text-blue-600">92%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-purple-50 rounded-xl">
                      <span className="font-medium text-purple-900">Patient Satisfaction</span>
                      <span className="text-2xl font-bold text-purple-600">96%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <button 
          onClick={handleNewAssessment}
          className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 shadow-lg border border-blue-200 hover:shadow-xl transition-all duration-300 hover:scale-105 group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-500 rounded-xl group-hover:bg-blue-600 transition-colors">
              <Plus className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-bold text-blue-900 text-lg">New Assessment</h3>
              <p className="text-blue-600">Create clinical note</p>
            </div>
          </div>
        </button>
        
        <button 
          onClick={handleNewCarePlan}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 shadow-lg border border-green-200 hover:shadow-xl transition-all duration-300 hover:scale-105 group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500 rounded-xl group-hover:bg-green-600 transition-colors">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-bold text-green-900 text-lg">Care Plans</h3>
              <button 
                onClick={() => actions.setActiveTab('careplans')}
                className="text-green-600 hover:underline"
              >
                View all plans
              </button>
            </div>
          </div>
        </button>
        
        <button className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 shadow-lg border border-purple-200 hover:shadow-xl transition-all duration-300 hover:scale-105 group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-500 rounded-xl group-hover:bg-purple-600 transition-colors">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-bold text-purple-900 text-lg">Alerts</h3>
              <p className="text-purple-600">View notifications</p>
            </div>
          </div>
        </button>

        <button className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 shadow-lg border border-orange-200 hover:shadow-xl transition-all duration-300 hover:scale-105 group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-500 rounded-xl group-hover:bg-orange-600 transition-colors">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-bold text-orange-900 text-lg">Reports</h3>
              <p className="text-orange-600">Generate reports</p>
            </div>
          </div>
        </button>
      </div>

      {/* Modals */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[96vh] overflow-hidden flex flex-col border border-gray-200">
            {/* Enterprise Header */}
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white px-10 py-7 border-b-2 border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Stethoscope className="w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-extrabold tracking-tight mb-1">
                      {assessmentModalType === 'anacc' ? 'AN-ACC Assessment' : ('Clinical Assessment')}
                    </h2>
                    <p className="text-gray-300 text-base font-medium">
                      {assessmentModalType === 'anacc' 
                        ? 'Australian National Aged Care Classification System' 
                        : ('Comprehensive clinical evaluation and documentation')}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => {
                    setShowAssessmentModal(false);
                    setSelectedResident(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-10 rounded-lg p-2.5 transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 bg-gradient-to-b from-gray-50 to-white">
              <div className="p-10">
                {assessmentModalType === 'anacc' ? (
                // Render AN-ACC Assessment Component
                <div>
                  {!selectedResident ? (
                    // Resident Selection Step
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Resident for AN-ACC Assessment
                        </label>
                        <select 
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onChange={(e) => {
                            if (e.target.value) {
                              setSelectedResident(e.target.value);
                            }
                          }}
                          value={selectedResident || ''}
                        >
                          <option value="">Select resident...</option>
                          {clinicalNotes.map(note => (
                            <option key={note.id} value={note.id}>
                              {note.residentName} - {note.room}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex justify-end gap-3 mt-6">
                        <button 
                          onClick={() => {
                            setShowAssessmentModal(false);
                            setSelectedResident(null);
                          }}
                          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // AN-ACC Assessment Component
                    <div>
                      <div className="mb-4 flex items-center justify-between">
                        <button
                          onClick={() => setSelectedResident(null)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-2"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Change Resident
                        </button>
                      </div>
                      <ANACCAssessment residentId={selectedResident} />
                    </div>
                  )}
                </div>
              ) : (
                  // Enterprise Health/Clinical Assessment Form
                  <div className="space-y-6">
                    {/* Section 1: Basic Information */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                      <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-xl font-extrabold text-gray-900 truncate">Participant Information</h3>
                          <p className="text-gray-500 text-sm mt-1">Select participant and assessment type</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            {'Resident'} <span className="text-red-600 font-normal normal-case">*</span>
                          </label>
                          <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                            <option>Select {'resident'}...</option>
                            {clinicalNotes.map(note => (
                              <option key={note.id} value={note.id}>{note.residentName} - {note.room}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Assessment Type <span className="text-red-600 font-normal normal-case">*</span>
                          </label>
                          <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                            {false ? (
                              <>
                                <option>Functional Capacity Assessment</option>
                                <option>Health Review Assessment</option>
                                <option>Goal-Based Health Evaluation</option>
                                <option>Support Needs Assessment</option>
                              </>
                            ) : (
                              <>
                                <option>Routine Clinical Assessment</option>
                                <option>Fall Risk Assessment (FRAT)</option>
                                <option>Cognitive Assessment (MMSE)</option>
                                <option>Nutrition Assessment (MUST)</option>
                                <option>Pain Assessment (Abbey Scale)</option>
                                <option>Pressure Risk Assessment (Braden Scale)</option>
                              </>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Assessment Details */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                      <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-xl font-extrabold text-gray-900 truncate">Assessment Details</h3>
                          <p className="text-gray-500 text-sm mt-1">Assessment date and assessor information</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Assessment Date <span className="text-red-600 font-normal normal-case">*</span>
                          </label>
                          <input 
                            type="date"
                            className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base"
                            defaultValue={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Assessed By <span className="text-red-600 font-normal normal-case">*</span>
                          </label>
                          <input 
                            type="text"
                            className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 placeholder:text-gray-400 text-base"
                            placeholder="Enter assessor name..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Findings */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                      <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <Target className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-xl font-extrabold text-gray-900 truncate">Assessment Findings</h3>
                          <p className="text-gray-500 text-sm mt-1">Document clinical observations and risk factors</p>
                        </div>
                      </div>
                      <div className="space-y-7">
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Clinical Observations
                          </label>
                          <textarea 
                            rows={6}
                            className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md resize-none hover:border-gray-400 placeholder:text-gray-400 text-base leading-relaxed"
                            placeholder="Document clinical observations, vital signs, and physical examination findings..."
                          ></textarea>
                        </div>
                        {false && (
                          <div>
                            <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                              Functional Capacity
                            </label>
                            <textarea 
                              rows={5}
                              className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md resize-none hover:border-gray-400 placeholder:text-gray-400 text-base leading-relaxed"
                              placeholder="Document functional capacity, independence levels, and support requirements..."
                            ></textarea>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-4 uppercase tracking-wide">
                            Risk Factors Identified
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {false ? (
                              <>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Health Risk</span>
                                </label>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Safety Risk</span>
                                </label>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Social Isolation</span>
                                </label>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Financial Risk</span>
                                </label>
                              </>
                            ) : (
                              <>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Fall Risk</span>
                                </label>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Pressure Risk</span>
                                </label>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Nutrition Risk</span>
                                </label>
                                <label className="flex items-center gap-3 p-5 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:shadow-md transition-all bg-white group">
                                  <input type="checkbox" className="w-5 h-5 text-blue-600 rounded border-2 border-gray-300 focus:ring-blue-500 group-hover:border-blue-500" />
                                  <span className="text-sm font-bold text-gray-800">Cognitive Decline</span>
                                </label>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 4: Recommendations */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                      <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-xl font-extrabold text-gray-900 truncate">Recommendations & Next Steps</h3>
                          <p className="text-gray-500 text-sm mt-1">Document interventions and review schedule</p>
                        </div>
                      </div>
                      <div className="space-y-7">
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Recommended Interventions
                          </label>
                          <textarea 
                            rows={6}
                            className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md resize-none hover:border-gray-400 placeholder:text-gray-400 text-base leading-relaxed"
                            placeholder="Document recommended interventions, treatments, or support strategies..."
                          ></textarea>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                          <div>
                            <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                              Review Date
                            </label>
                            <input 
                              type="date"
                              className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                              Priority Level
                            </label>
                            <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                              <option>Low</option>
                              <option>Medium</option>
                              <option>High</option>
                              <option>Urgent</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Fixed Footer */}
              <div className="bg-gradient-to-r from-gray-50 to-white border-t-2 border-gray-200 px-10 py-7 flex justify-end gap-5 shadow-xl">
                <button 
                  onClick={() => {
                    setShowAssessmentModal(false);
                    setSelectedResident(null);
                  }}
                  className="px-10 py-4 border-2 border-gray-400 text-gray-700 rounded-xl hover:bg-gray-100 hover:border-gray-500 transition-all font-extrabold shadow-md hover:shadow-lg text-base"
                >
                  Cancel
                </button>
                <button className="px-10 py-4 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-900 hover:to-black transition-all font-extrabold shadow-xl hover:shadow-2xl text-base">
                  {'Save Clinical Assessment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCarePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[96vh] overflow-hidden flex flex-col border border-gray-200">
            {/* Enterprise Header */}
            <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 text-white px-10 py-7 border-b-2 border-purple-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <FileText className="w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-extrabold tracking-tight mb-1">
                      {'New Care Plan'}
                    </h2>
                    <p className="text-purple-200 text-base font-medium">
                      {'Create a comprehensive clinical care plan outlining resident care goals, interventions, and outcomes'}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCarePlanModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-10 rounded-lg p-2.5 transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1 bg-gradient-to-b from-gray-50 to-white">
              <div className="p-10">
                <div className="space-y-6">
                  {/* Section 1: Basic Information */}
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                    <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-extrabold text-gray-900 truncate">{'Resident'} Information</h3>
                        <p className="text-gray-500 text-sm mt-1">Select participant and plan type</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                          {'Resident'} <span className="text-red-600 font-normal normal-case">*</span>
                        </label>
                        <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                          <option>Select {'resident'}...</option>
                          {clinicalNotes.map(note => (
                            <option key={note.id} value={note.id}>{note.residentName} - {note.room}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                          {'Care Plan'} Type <span className="text-red-600 font-normal normal-case">*</span>
                        </label>
                        <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                          {false ? (
                            <>
                              <option>Core Supports Plan</option>
                              <option>Capacity Building Plan</option>
                              <option>Capital Supports Plan</option>
                              <option>Comprehensive Support Plan</option>
                              <option>Goal-Based Support Plan</option>
                            </>
                          ) : (
                            <>
                              <option>Comprehensive Care Plan</option>
                              <option>Dementia Care Plan</option>
                              <option>Palliative Care Plan</option>
                              <option>Rehabilitation Plan</option>
                              <option>Behavioral Support Plan</option>
                              <option>Wound Care Plan</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Goals & Objectives */}
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                    <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-extrabold text-gray-900 truncate">{'Care Goals & Objectives'}</h3>
                        <p className="text-gray-500 text-sm mt-1">Define primary and additional goals</p>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                          Primary {'Objective'} <span className="text-red-600 font-normal normal-case">*</span>
                        </label>
                        <input 
                          type="text"
                          className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 placeholder:text-gray-400 text-base"
                          placeholder={"e.g., Maintain optimal blood glucose levels"}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-4 uppercase tracking-wide">
                          Additional {'Objectives'}
                        </label>
                        <div className="space-y-4">
                          {[1, 2, 3].map((i) => (
                            <input 
                              key={i}
                              type="text"
                              className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 placeholder:text-gray-400 text-base"
                              placeholder={`${'Objective'} ${i + 1}...`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Interventions & Supports */}
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                    <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-extrabold text-gray-900 truncate">{'Care Interventions'}</h3>
                        <p className="text-gray-500 text-sm mt-1">Define support strategies and implementation details</p>
                      </div>
                    </div>
                    <div className="space-y-7">
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                          {'Intervention'} Description <span className="text-red-600 font-normal normal-case">*</span>
                        </label>
                        <textarea 
                          rows={6}
                          className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md resize-none hover:border-gray-400 placeholder:text-gray-400 text-base leading-relaxed"
                          placeholder={"Describe the care interventions, treatments, and activities planned to achieve care objectives..."}
                        ></textarea>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Frequency
                          </label>
                          <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                            <option>Daily</option>
                            <option>Weekly</option>
                            <option>Fortnightly</option>
                            <option>Monthly</option>
                            <option>As Required</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Duration
                          </label>
                          <input 
                            type="text"
                            className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 placeholder:text-gray-400 text-base"
                            placeholder="e.g., 30 minutes"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                            Responsible Staff
                          </label>
                          <select className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base">
                            <option>Registered Nurse</option>
                            <option>Enrolled Nurse</option>
                            <option>Personal Care Worker</option>
                            <option>Allied Health Professional</option>
                            <option>Support Worker</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Review & Monitoring */}
                  <div className="bg-white rounded-xl border-2 border-gray-200 p-8 shadow-lg overflow-hidden">
                    <div className="flex items-center gap-4 mb-8 pb-5 border-b-2 border-gray-200">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-extrabold text-gray-900 truncate">Review & Monitoring Schedule</h3>
                        <p className="text-gray-500 text-sm mt-1">Set review dates and evaluation criteria</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-7 mb-7">
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                          Plan Start Date <span className="text-red-600 font-normal normal-case">*</span>
                        </label>
                        <input 
                          type="date"
                          className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base"
                          defaultValue={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                          Next Review Date <span className="text-red-600 font-normal normal-case">*</span>
                        </label>
                        <input 
                          type="date"
                          className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md hover:border-gray-400 text-base"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-extrabold text-gray-900 mb-3 uppercase tracking-wide">
                        Monitoring & Evaluation Criteria
                      </label>
                      <textarea 
                        rows={5}
                        className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-purple-200 focus:border-purple-600 transition-all bg-white text-gray-900 font-semibold shadow-md resize-none hover:border-gray-400 placeholder:text-gray-400 text-base leading-relaxed"
                        placeholder="Describe how progress will be measured and evaluated..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Fixed Footer */}
            <div className="bg-gradient-to-r from-gray-50 to-white border-t-2 border-gray-200 px-10 py-7 flex justify-end gap-5 shadow-xl">
              <button 
                onClick={() => setShowCarePlanModal(false)}
                className="px-10 py-4 border-2 border-gray-400 text-gray-700 rounded-xl hover:bg-gray-100 hover:border-gray-500 transition-all font-extrabold shadow-md hover:shadow-lg text-base"
              >
                Cancel
              </button>
              <button className="px-10 py-4 bg-gradient-to-r from-indigo-800 to-purple-800 text-white rounded-xl hover:from-indigo-900 hover:to-purple-900 transition-all font-extrabold shadow-xl hover:shadow-2xl text-base">
                {'Create Care Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotesModal && selectedNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Clinical Notes - {selectedNote.residentName}</h2>
                <button 
                  onClick={() => setShowNotesModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Cognitive Assessment</h4>
                    <p className="text-blue-700">Score: {selectedNote.metrics.cognitive.score}/{selectedNote.metrics.cognitive.max}</p>
                    <p className="text-sm text-blue-600 capitalize">{selectedNote.metrics.cognitive.status}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                    <h4 className="font-semibold text-green-900 mb-2">Physical Health</h4>
                    <p className="text-green-700">Score: {selectedNote.metrics.physical.score}/{selectedNote.metrics.physical.max}</p>
                    <p className="text-sm text-green-600 capitalize">{selectedNote.metrics.physical.status}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-2">Clinical Notes</h4>
                  <p className="text-gray-700 leading-relaxed">{selectedNote.notes}</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl">
                  <h4 className="font-semibold text-gray-900 mb-2">Recommendations</h4>
                  <ul className="space-y-2">
                    {selectedNote.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button 
                  onClick={() => setShowNotesModal(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button className="px-6 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors">
                  Edit Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Tools Modal */}
      {showAssessmentTools && (
        <AssessmentTools onClose={() => setShowAssessmentTools(false)} />
      )}

      {/* Incident Reporting Modal */}
      {showIncidentReporting && (
        <IncidentReporting onClose={() => setShowIncidentReporting(false)} />
      )}

      {/* Palliative Care Templates Modal */}
      {showPalliativeCareTemplates && (
        <PalliativeCareTemplates onClose={() => setShowPalliativeCareTemplates(false)} />
      )}
    </div>
  );
};

export default Clinical;
